import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Search, Save, Pencil, Users, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("en-AU", { dateStyle: "medium" }); }
  catch { return iso; }
}

function TierPill({ tier, role }) {
  if (role === "admin") return <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-brand-orange text-brand-orange">admin</span>;
  const paid = tier === "paid_member";
  return <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border ${paid ? "border-emerald-600 text-emerald-800 bg-emerald-50" : "border-slate-300 text-slate-600 bg-slate-50"}`}>{tier || "free"}</span>;
}

function MemberNoteCell({ userId, initial, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(initial || ""); }, [initial]);
  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/users/${userId}/note`, { note: val });
      toast.success("Note saved"); setEditing(false); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input data-testid={`members-note-input-${userId}`} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setVal(initial || ""); }}} className="border border-slate-300 px-2 py-1 text-xs w-64 focus:outline-none focus:border-brand-blue" autoFocus />
        <button data-testid={`members-note-save-${userId}`} onClick={save} disabled={busy} className="text-xs uppercase tracking-widest font-semibold text-brand-orange disabled:opacity-50">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}</button>
      </div>
    );
  }
  return (
    <button data-testid={`members-note-cell-${userId}`} onClick={() => setEditing(true)} className="text-left w-full text-xs text-slate-600 hover:text-brand-orange group inline-flex items-start gap-1">
      <span className="flex-1">{initial || <span className="italic text-slate-400 group-hover:text-brand-orange">Private note…</span>}</span>
      <Pencil className="w-3 h-3 mt-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0" />
    </button>
  );
}

export default function AdminMembers() {
  const { user, status: authStatus } = useAuth();
  const [state, setState] = useState({ loading: true, error: null });
  const [members, setMembers] = useState([]);
  const [totals, setTotals] = useState({ total: 0, filtered_total: 0 });
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("all");

  // Debounce search input to avoid a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQDeb(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = () => {
    setState({ loading: true, error: null });
    const params = new URLSearchParams({ q: qDeb, tier, status, limit: "500" });
    axios.get(`${API}/admin/members?${params.toString()}`)
      .then(({ data }) => {
        setMembers(data.members || []);
        setTotals({ total: data.total || 0, filtered_total: data.filtered_total || 0 });
        setState({ loading: false, error: null });
      })
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Could not load members." }));
  };

  useEffect(() => { if (authStatus === "authed" && user?.role === "admin") load(); /* eslint-disable-next-line */ }, [authStatus, user, qDeb, tier, status]);

  const stats = useMemo(() => {
    const paid = members.filter((m) => m.tier === "paid_member").length;
    const free = members.length - paid;
    return { paid, free, matched: members.length, total: totals.total };
  }, [members, totals]);

  if (authStatus === "checking") return <div className="min-h-[60vh] flex items-center justify-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking session…</div>;
  if (authStatus !== "authed") return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/member/hub" replace />;

  return (
    <div data-testid="admin-members-page" className="bg-slate-50 min-h-screen py-14 px-6 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-start justify-between mb-8 gap-4">
          <div>
            <div className="eyebrow text-brand-orange mb-2">Admin</div>
            <h1 className="font-serif text-4xl lg:text-5xl text-brand-blue leading-tight">Members directory.</h1>
            <p className="text-slate-600 mt-3 max-w-2xl">Everyone who has ever created an account with All Things Social Impact. Search by name, email or note; filter by tier or subscription status.</p>
          </div>
          <button data-testid="members-refresh-btn" onClick={load} className="btn-secondary whitespace-nowrap"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div data-testid="stat-total" className="bg-white border border-slate-200 p-6"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500"><Users className="w-3.5 h-3.5" /> {stats.matched === stats.total ? "Total accounts" : "Matching accounts"}</div><div className="mt-3 font-serif text-3xl text-brand-blue">{stats.matched}{stats.matched !== stats.total && <span className="text-base text-slate-400 ml-2">of {stats.total}</span>}</div></div>
          <div data-testid="stat-paid" className="bg-white border border-slate-200 p-6"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500"><Crown className="w-3.5 h-3.5" /> Paid members</div><div className="mt-3 font-serif text-3xl text-brand-blue">{stats.paid}</div></div>
          <div data-testid="stat-free" className="bg-white border border-slate-200 p-6"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500"><Users className="w-3.5 h-3.5" /> Free accounts</div><div className="mt-3 font-serif text-3xl text-brand-blue">{stats.free}</div></div>
        </div>

        <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="members-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, note…" className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:outline-none focus:border-brand-blue" />
          </div>
          <div><label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Tier</label>
            <select data-testid="members-filter-tier" value={tier} onChange={(e) => setTier(e.target.value)} className="text-sm border border-slate-300 px-2 py-2 focus:outline-none focus:border-brand-blue">
              <option value="all">All</option><option value="paid_member">Paid</option><option value="free">Free</option>
            </select>
          </div>
          <div><label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Status</label>
            <select data-testid="members-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm border border-slate-300 px-2 py-2 focus:outline-none focus:border-brand-blue">
              <option value="all">Any</option><option value="active">Active</option><option value="canceling">Canceling</option><option value="canceled">Canceled</option><option value="past_due">Past due</option>
            </select>
          </div>
        </div>

        {state.error && <div className="mb-8 border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-900">{state.error}</div>}
        {state.loading ? <div className="py-24 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading members…</div> : (
          members.length === 0 ? <div className="bg-white border border-slate-200 p-8 text-center text-slate-500 text-sm">No members match your filters.</div> : (
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest font-semibold text-slate-500 border-b border-slate-200">
                    <th className="px-4 py-3">Joined</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Tier</th><th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 min-w-[240px]">Private note</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-slate-100 hover:bg-slate-50/50 align-top">
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{fmtDate(m.created_at)}</td>
                      <td className="px-4 py-3 text-slate-800 font-semibold">{m.name || "-"}</td>
                      <td className="px-4 py-3"><a href={`mailto:${m.email}`} className="text-xs text-brand-blue hover:underline">{m.email}</a></td>
                      <td className="px-4 py-3"><TierPill tier={m.tier} role={m.role} /></td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {m.subscription_status || "-"}
                        {m.subscription_cancel_at_period_end && <div className="text-amber-700 text-[10px] uppercase tracking-widest mt-0.5">Canceling</div>}
                      </td>
                      <td className="px-4 py-3"><MemberNoteCell userId={m.id} initial={m.admin_note} onSaved={load} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}
